/**
 * View Toggles Component
 * Quick-access toggles for filtering OKRs by organizational level
 */

import React from 'react';
import { Building2, Landmark, Users, User } from 'lucide-react';

interface ViewTogglesProps {
  activeTypes: Set<string>;
  onToggle: (type: string) => void;
}

export const ViewToggles: React.FC<ViewTogglesProps> = ({ activeTypes, onToggle }) => {
  const viewOptions = [
    {
      type: 'company',
      label: 'Company',
      icon: Building2,
      activeColors: 'bg-blue-600 text-white border-blue-700 shadow-md',
      inactiveColors: 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50',
    },
    {
      type: 'department',
      label: 'Department',
      icon: Landmark,
      activeColors: 'bg-amber-600 text-white border-amber-700 shadow-md',
      inactiveColors: 'bg-white text-amber-600 border-amber-300 hover:bg-amber-50',
    },
    {
      type: 'team',
      label: 'Team',
      icon: Users,
      activeColors: 'bg-green-600 text-white border-green-700 shadow-md',
      inactiveColors: 'bg-white text-green-600 border-green-300 hover:bg-green-50',
    },
    {
      type: 'individual',
      label: 'Individual',
      icon: User,
      activeColors: 'bg-purple-600 text-white border-purple-700 shadow-md',
      inactiveColors: 'bg-white text-purple-600 border-purple-300 hover:bg-purple-50',
    },
  ];

  const handleToggle = (type: string) => {
    onToggle(type);
  };

  const isActive = (type: string): boolean => {
    return activeTypes.has(type);
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-stone-700 mr-1">View:</span>
      {viewOptions.map(({ type, label, icon: Icon, activeColors, inactiveColors }) => {
        const active = isActive(type);
        return (
          <button
            key={type}
            onClick={() => handleToggle(type)}
            className={`
              flex items-center gap-2 px-3 py-1.5 rounded-lg border-2
              transition-all duration-200 text-sm font-medium
              ${active ? activeColors : inactiveColors}
            `}
            title={`${active ? 'Hide' : 'Show'} ${label} OKRs`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        );
      })}
    </div>
  );
};
